<script setup>
import { ref, computed, watch, watchEffect, onUnmounted, provide, defineAsyncComponent } from "vue"
import { useEventListener, useBreakpoints, breakpointsTailwind } from "@vueuse/core"
import { useCanny } from '@/hooks/useCanny'
import { useInventoryPilot } from '@/helpers/featureFlags'
import { useRoute } from "vue-router"
import AdditionalBar from "./components/AdditionalBar.vue"
import { useGlobalStore } from "@/stores/GlobalStore"
import { useStrapiStore } from "@/stores/StrapiStore"
import { useConfigStore } from "@/stores/ConfigStore"
import { useUserStore } from "@/stores/UserStore"
import Header from "./Header/index.vue"
import AddressDialog from '@/components/Address/AddressDialog.vue'
import AddressAlert from '@/components/Address/AddressAlert.vue'
import { useSidebarFit } from '@/hooks/useSidebarFit'

const ReactivateAccount = defineAsyncComponent(() => import('@/components/ReactivateAccount.vue'))
const AdminBanner = defineAsyncComponent(() => import("./components/AdminBanner.vue"))
const NotificationBar = defineAsyncComponent(() =>
  import("./components/NotificationBar.vue")
);
// Two rails, picked by the design flag. The legacy file is untouched: a
// dispatcher here means a legacy user's sidebar can't regress from this work,
// and neither bundle pays for the other (both are async).
const Sidebar = defineAsyncComponent(() => import("./Sidebar/index.vue"))
const NewSidebar = defineAsyncComponent(() => import("./Sidebar/NewSidebar/index.vue"))
const CannyDialog = defineAsyncComponent(() => import("./CannyDialog/index.vue"))
const NewDesignWelcomeModal = defineAsyncComponent(() => import("@/components/NewDesign/NewDesignWelcomeModal.vue"))
const NewDesignTour = defineAsyncComponent(() => import("@/components/NewDesign/NewDesignTour.vue"))

const globalStore = useGlobalStore()
const strapiStore = useStrapiStore()
const userStore = useUserStore()
const isInventoryPilot = useInventoryPilot()
const configStore = useConfigStore()
const route = useRoute()

// Tag the root so new-design-only styles (e.g. AG Grid editable-cell state
// borders) can scope to `.is-new-design ...` without touching the old grid.
watchEffect(() => {
  document.documentElement.classList.toggle('is-new-design', userStore.isNewDesign)
})

const sidebar = computed(() => globalStore.sidebar);
const windowWidth = computed(() => globalStore.windowWidth);
const showAdminBanner = computed(() => globalStore.showAdminBanner);
const showSystemNotificationBar = computed(
  () => Object.keys(strapiStore.notificationStatus).length !== 0
);
const systemNotificationBar = computed(() => strapiStore.notificationStatus);
const notificationBarHeight = ref(40);
const showingAside = computed(() => {
  const excludedRoutes = [
    "export-batch",
    "create-batch",
    "AddTypes",
    "AddSides",
    "AddUpload",
    "AddCert",
  ];
  return !excludedRoutes.some(
    (routePart) => route.fullPath.includes(routePart) || route.name === routePart
  );
});

const showFeedbackDialog = ref({
  open: false,
});

useCanny()

const closeSidebar = () => {
  if (windowWidth.value < 1280) {
    globalStore.sidebar = false;
    localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
  }
};

const closeTabletSidebar = () => {
  if (windowWidth.value >= 768 && windowWidth.value < 1280) {
    if (globalStore.sidebar) {
      globalStore.sidebar = false;
      localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
    }
  }
}

const handleAddAddress = () => {
  globalStore.showAddressDialog = true
}

const effectiveBarHeight = computed(() =>
  windowWidth.value >= 768 ? 40 : notificationBarHeight.value
);

provide("showFeedbackDialog", showFeedbackDialog);
provide("showingAside", showingAside);
provide("closeSidebar", closeSidebar);
provide("showSystemNotificationBar", showSystemNotificationBar);
provide("systemNotificationBar", systemNotificationBar);
provide("notificationBarHeight", notificationBarHeight);
provide("effectiveBarHeight", effectiveBarHeight);


// Sidebar geometry — the content padding has to agree with the rail's real
// width, including the short-viewport icons-only override, so both read the
// same composable rather than each guessing.
const isNewSidebar = computed(() => userStore.isNewDesign)
const { forceIconsOnly } = useSidebarFit()
const newSidebarExpanded = computed(() => isNewSidebar.value && globalStore.sidebar && !forceIconsOnly.value)

const mainContentScroll = ref(null)
const isCreditsBarMode = useBreakpoints(breakpointsTailwind).smaller('lg')

const SHOW_AT = 8
const HIDE_AT = 56
const TOGGLE_LOCK_MS = 350
let toggleLockTimer = null
let toggleLocked = false
const setCreditsBarHidden = (hidden) => {
  if (globalStore.creditsBarHidden === hidden) return
  globalStore.creditsBarHidden = hidden
  toggleLocked = true
  if (toggleLockTimer) clearTimeout(toggleLockTimer)
  toggleLockTimer = setTimeout(() => { toggleLocked = false }, TOGGLE_LOCK_MS)
}

const onContentScroll = (e) => {
  const el = e.target
  if (!el || typeof el.scrollTop !== 'number') return

  // Desktop / credits menu open → always shown. Collapse explicitly disabled → leave as-is.
  if (!isCreditsBarMode.value || globalStore.creditsMenuOpen) {
    globalStore.creditsBarHidden = false
    return
  }
  if (globalStore.creditsCollapseDisabled || toggleLocked) return

  if (el !== mainContentScroll.value && !el.matches?.('[data-credits-scroll-root]')) return

  const top = el.scrollTop
  if (top <= SHOW_AT) setCreditsBarHidden(false)
  else if (top > HIDE_AT) setCreditsBarHidden(true)
}

useEventListener(mainContentScroll, 'scroll', onContentScroll, { capture: true, passive: true })
useEventListener(document, 'scroll', onContentScroll, { capture: true, passive: true })

watch(() => route.fullPath, () => { globalStore.creditsBarHidden = false })
watch(() => globalStore.creditsMenuOpen, (open) => { if (open) globalStore.creditsBarHidden = false })
watch(isCreditsBarMode, (mobile) => { if (!mobile) globalStore.creditsBarHidden = false })

onUnmounted(() => {
  if (toggleLockTimer) clearTimeout(toggleLockTimer)
})

/**
 * Height of everything fixed at the top of the page, as a NUMBER.
 *
 * One calculation, two consumers: the page content's own top padding (below)
 * and `globalStore.appChromeHeight`, which anything else that must sit under the
 * chrome reads — the docked All Batches panel, for one. It used to be inlined
 * here as a string and hard-coded as `48` at the panel, so any session with an
 * extra bar had the panel starting above the bottom of the header and covering
 * it (the panel outranks the header on z-index).
 */
const chromeHeight = computed(() => {
  const mainRowHeight = 48; // h-12
  const mobileBarHeight = isCreditsBarMode.value && configStore.config?.hasActiveSubscription
    ? (globalStore.creditsBarHidden ? 0 : 40)
    : 0;
  const headerHeight = mainRowHeight + mobileBarHeight;

  if (showSystemNotificationBar.value) return effectiveBarHeight.value + headerHeight;
  if (showAdminBanner.value) return 40 + headerHeight;

  return headerHeight;
});

const sectionPaddingTop = computed(() => `${chromeHeight.value}px`);

watchEffect(() => { globalStore.appChromeHeight = chromeHeight.value });

const isBatchDetailsFlow = computed(
  () => route.matched?.length > 1 && route.matched[1].name === "BatchDetailsFlow"
)

const showAddressBanner = () => {
  const shouldShow = !isBatchDetailsFlow.value &&
    (!userStore.user?.address_1 || !userStore.user?.city || !userStore.user?.state || !userStore.user?.postal_code)
  globalStore.showAddressBanner = shouldShow
  return shouldShow
}

watchEffect(() => {
  const isHomepageOrRelated = [
    "Homepage",
    "Batches",
    "Inventory",
    "Connections",
  ].includes(route.name);

  if (!localStorage?.sidebar) {
    globalStore.sidebar = isHomepageOrRelated;
  }
});
</script>

<template>
  <NotificationBar />
  <AdminBanner />
  <section
    class="h-full flex pt-12 transition-all duration-300 overflow-hidden"
    :style="{ paddingTop: sectionPaddingTop }"
    :class="{
      'xl:!pl-72': sidebar && !isNewSidebar,
      'md:pl-[60px]': showingAside && !isNewSidebar,
      'md:!pl-72': sidebar && !isNewSidebar,
      'md:pl-14': isNewSidebar && showingAside,
      'md:!pl-[262px]': isNewSidebar && newSidebarExpanded,
      'xl:!pl-[262px]': isNewSidebar && newSidebarExpanded,
    }"
  >
    <section class="main-layout" v-click-outside="closeTabletSidebar">
      <Header />
      <NewSidebar v-if="userStore.isNewDesign" />
      <Sidebar v-else />
    </section>
    <section
      id="main-content-scroll"
      ref="mainContentScroll"
      class="flex flex-col h-full flex-grow overflow-x-clip overflow-y-auto"
      :class="{
        'bg-[#F9FAFB]': route.path === '/connections/ebay/connect' || route.path === '/connections/ebay/connected' || route.path === '/connections/whatnot/connect'  || route.path === '/connections/whatnot/connected'
      }"
    >
      <AdditionalBar
        v-if="showAddressBanner()"
        :text="`Additional information is required. Our payment processor requires a complete address. Please enter your home or business address.`"
        :buttonText="`Add Address`"
        :buttonIcon="`heroicons:arrow-right`"
        @handleButtonClick="handleAddAddress"
      />
      <AddressDialog v-if="globalStore.showAddressDialog" />
      <AddressAlert v-if="globalStore.showAddressAlert" />
      <RouterView />
    </section>
  </section>

  <CannyDialog />

  <!-- Both are invitations to the redesign — hidden entirely until the
       user is on the inventory pilot list (`constants/pilots.js`). -->
  <NewDesignWelcomeModal v-if="isInventoryPilot" />

  <NewDesignTour v-if="isInventoryPilot" />

  <!-- Global Modal - Shows on all pages based on subscription status -->
  <section
    class="
      absolute top-0 left-0 w-full h-full flex-grow min-h-full flex flex-col z-[99] bg-no-repeat bg-cover
      bg-[url('@/assets/images/batches-frame.png')]
    "
    v-if="!configStore.reactivateAccountModal && !['SettingsPlans', 'Homepage'].includes(route.name)"
  >
    <ReactivateAccount />
  </section>
</template>
